/**
 * The live view's messages to the page that embeds it (2026-09-23).
 *
 * viewer-keys.ts runs inside the workstation's noVNC viewer, which the sign-in
 * window embeds cross-origin. It tells this page whether the window has the
 * keyboard and whether Caps Lock is on, and hands over every paste the person
 * makes in it, because only this page holds the workstation credential for the
 * D19a paste route. This page writes the text into the remote browser's own
 * clipboard (press:false) and answers; the viewer then presses Ctrl+V where
 * the person is typing. It asks this page to clear the clipboard afterwards.
 * The viewer carries pastes only once this page has said it handles them
 * (VIEWER_MSG_EMBEDDER), so an older page never leaves a paste unanswered.
 *
 * Its own module, with no imports, so the window and its tests use the real
 * reader without loading the sign-in helpers (and Firebase) behind it. The
 * message names must match viewer-keys.ts (MSG_PASTE, MSG_PASTE_DONE,
 * MSG_STATE, MSG_EMBEDDER, MSG_CLEAR); a test reads both files to keep them
 * equal.
 */

export const VIEWER_MSG_PASTE = 'prest0n:viewer-paste';
export const VIEWER_MSG_PASTE_DONE = 'prest0n:viewer-paste-done';
export const VIEWER_MSG_STATE = 'prest0n:viewer-state';
/** This page tells the viewer it carries pastes. */
export const VIEWER_MSG_EMBEDDER = 'prest0n:viewer-embedder';
/** The viewer asks this page to replace the workstation clipboard with a space. */
export const VIEWER_MSG_CLEAR = 'prest0n:viewer-clear';
/** What replaces the workstation clipboard after a paste (the route refuses empty text). */
export const VIEWER_CLEAR_TEXT = ' ';

/** The hello this page sends the viewer, on load and whenever the viewer reports itself. */
export const VIEWER_EMBEDDER_HELLO = { type: VIEWER_MSG_EMBEDDER, v: 1, paste: true } as const;

/** What the live view reports about itself (viewer-keys.ts postState). */
export interface ViewerState {
  focused: boolean;
  capsLock: boolean;
  notice?: 'paste-too-large';
}

/**
 * Read one message from the live view, or null when it is not one: the
 * workstation's own origin and a known shape. (The caller also checks that the
 * message came from its own frame.) Pure.
 */
export function readViewerMessage(
  data: unknown,
  eventOrigin: string,
  viewerOrigin: string,
):
  | { kind: 'paste'; id: number; text: string }
  | { kind: 'state'; state: ViewerState }
  | { kind: 'clear' }
  | null {
  if (!eventOrigin || eventOrigin !== viewerOrigin) return null;
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (d.type === VIEWER_MSG_PASTE) {
    if (typeof d.id !== 'number' || typeof d.text !== 'string' || !d.text) return null;
    return { kind: 'paste', id: d.id, text: d.text };
  }
  if (d.type === VIEWER_MSG_CLEAR) return { kind: 'clear' };
  if (d.type === VIEWER_MSG_STATE) {
    const notice = d.notice === 'paste-too-large' ? d.notice : undefined;
    return {
      kind: 'state',
      state: { focused: d.focused === true, capsLock: d.capsLock === true, ...(notice ? { notice } : {}) },
    };
  }
  return null;
}
